import React, { useCallback, useEffect } from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import EditorExpandIcon from '@atlaskit/icon/glyph/editor/expand';
import SectionMessage from '@atlaskit/section-message';

import { trackEvent } from 'src/lib/analytics';
import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';

import ErrorMessage from 'src/views/components/ErrorMessage';
import AutoscalableInputsArray from 'src/views/components/Form/AutoscalableInputsArray';
import Field from 'src/views/components/Form/Field';
import FieldLabel from 'src/views/components/Form/FieldLabel';
import { FormProvider, useForm } from 'src/views/components/Form/form';
import ModalButtons from 'src/views/components/ModalButtons';
import ModalContainer from 'src/views/components/ModalContainer';
import { JiraUserSelect, PdUsersSelect } from 'src/views/components/select';

const newItemValue = { pd_user: null, jira_user: null };
const isItemEmpty = ({ pd_user, jira_user }) => (!pd_user && !jira_user);

const jiraUserRawRecordToOption = u => ({
  key: u.key,
  label: `${u.displayName}${u.emailAddress ? ` (${u.emailAddress})` : ''}`,
  icon: Object.values(u.avatarUrls || {})[0],
  data: {
    jira_name: u.name,
    jira_account_id: u.accountId, //TODO: check with Jira Cloud if filed is correct
    jira_displayname: u.displayName,
    jira_email: u.emailAddress,
    jira_html_url: u.self
  },
});

const pdUserRawRecordToOption = u => ({
  label: u.summary,
  id: u.id,
  icon: u.avatar_url,
  data: {
    pd_id: u.id,
    pd_email: u.email,
    pd_html_url: u.html_url,
    pd_displayname: u.summary,
  }
});

const SingleUserMappingComponent = ({ name, values = {}, onChange, onBlur, touched = {}, errors = {} }) => {
  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <div style={{ width: '50%' }}>
        <Field errorMessage={ touched.pd_user && errors.pd_user }>
          <PdUsersSelect
            rawRecordToOption={pdUserRawRecordToOption}
            name={`${name}.pd_user`}
            value={values.pd_user}
            onChange={onChange}
            onBlur={onBlur}
            isClearable
          />
        </Field>
      </div>
      <div style={{ display: 'flex', flexFlow: 'row nowrap', margin: '-4px 10px', width: 50 }}><EditorExpandIcon size="xlarge"/></div>
      <div style={{ width: '50%' }}>
        <Field errorMessage={ touched.jira_user && errors.jira_user }>
          <JiraUserSelect
            rawRecordToOption={jiraUserRawRecordToOption}
            name={`${name}.jira_user`}
            value={values.jira_user}
            onChange={onChange}
            onBlur={onBlur}
            isClearable
          />
        </Field>
      </div>
    </div>
  );
};

const UserMappingsForm = () => {
  const { values,
    name,
    isSubmitting,
    isSubmitSuccessful,
    submitError,
    onChange,
    onBlur,
    touched,
    errors,
    onSubmit } = useForm();

  useEffect(() => {
    if (isSubmitSuccessful) jira.closeCurrentModal();
  }, [isSubmitSuccessful]);

  const onSubmitWithTracking = useCallback((props) => {
    trackEvent('settings_usermappings_linkusers', { label: 'create_user_mappings' });
    onSubmit(props);
  }, [onSubmit]);

  return (
    <form onSubmit={onSubmitWithTracking}>
      <div style={{ minHeight: 300 }}>
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ width: '50%' }}>
            <FieldLabel isRequired>Select PagerDuty user</FieldLabel>
          </div>
          <div style={{ display: 'flex', flexFlow: 'row nowrap', margin: '-4px 10px', width: 50 }}/>
          <div style={{ width: '50%' }}>
            <FieldLabel isRequired>Select Jira user</FieldLabel>
          </div>
        </div>

        <AutoscalableInputsArray
          name={name}
          values={values}
          touched={touched}
          errors={errors}
          onChange={onChange}
          onBlur={onBlur}
          newItemValue={ newItemValue }
          ItemComponent={ SingleUserMappingComponent }
          isItemEmpty={ isItemEmpty }
        />
        <div style={{ marginTop: 20 }}>
          <SectionMessage>
            <ul>
              <li>Each user can map himself during login or in the PagerDuty issue sidebar settings</li>
              <li>If you select user mapping that conflicts with already existing mappings, the new mapping will override the old one</li>
            </ul>
          </SectionMessage>
        </div>
        <div style={{ minHeight: 200 }}>
          { submitError && <ErrorMessage error={['Can not map users', submitError]}/> }
        </div>
      </div>
      <ModalButtons>
        <Button onClick={e => jira.closeCurrentModal()}>Cancel</Button>
        <LoadingButton type="submit" appearance="primary" isDisabled={values.length === 0} isLoading={isSubmitting}>Create user mappings</LoadingButton>
      </ModalButtons>
    </form>
  );
};

const createUsersMappings = async (mappings) => {
  const processedMappings = mappings.filter(x => x && x.pd_user && x.jira_user).map(m => ({
    ...m.pd_user.data,
    ...m.jira_user.data,
    created_by: 'admin'
  }));

  return integration.createUserMappingsBatch(processedMappings);
};

const onValidate = (values) => {
  return values.map(({ pd_user, jira_user }) => (
    {
      pd_user: (jira_user && !pd_user) ? 'Please select PagerDuty user' : undefined,
      jira_user: (pd_user && !jira_user) ? 'Please select Jira user' : undefined,
    }));
};

const LinkUsersModal = () => {
  return (
    <ModalContainer title="Link users">
      <FormProvider onSave={createUsersMappings} initValues={[]} onValidate={onValidate}>
        <UserMappingsForm/>
      </FormProvider>
    </ModalContainer>
  );
};

export default LinkUsersModal;
